import { FC, useState } from 'react'
import { IconHelpCircle } from '@tabler/icons-react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import Typography from '../../atoms/Typography'
import { PaymentMethod } from 'src/components/molecules/PaymentMethod'
import { useGetPaymentMethods } from 'src/enviroment/api/services/paymentMethodsApi'
import { ISubscriptionPlan, useChangePlan } from 'src/enviroment/api/services/subscriptionsApi'
import { enqueueSnackbar } from 'notistack'
import PaymentForm from '../PaymentForm'
import { AxiosError } from 'axios'

interface Props {
  subscription: ISubscriptionPlan
}

const UpgradePlanDialog: FC<Props> = ({ subscription }) => {
  const [opened, setOpened] = useState<boolean>(false)
  const [supportDialog, setSupportDialogOpen] = useState<boolean>(false)
  const [showAddPayment, setShowAddPayment] = useState<boolean>(false)
  const { data: methods, refetch: refetchMethods } = useGetPaymentMethods()
  const { mutateAsync: upgradePlan, isLoading } = useChangePlan()

  const handleDialogChange = (open: boolean) => {
    setOpened(open)
    if (!open) {
      setShowAddPayment(false)
    }
  }

  const doUpgrade = async () => {
    try {
      await upgradePlan({
        plan: subscription.plan,
        interval: subscription.interval,
      })
      enqueueSnackbar('Plan upgraded successfully', { variant: 'success' })
      setOpened(false)
    } catch (error) {
      console.error('Failed to upgrade plan:', error)
      if (error instanceof AxiosError && error.response?.data?.message) {
        enqueueSnackbar(`Failed to upgrade plan: ${error.response.data.message}`, { variant: 'error' })
      } else {
        enqueueSnackbar('Failed to upgrade plan', { variant: 'error' })
      }
    }
  }

  const handleUpgrade = async () => {
    // Skip payment method check for free plans
    if (subscription.priceInCents > 0 && !methods?.length) {
      setShowAddPayment(true)
      return
    }

    await doUpgrade()
  }

  const handlePaymentSuccess = async () => {
    await refetchMethods()
    setShowAddPayment(false)
    await doUpgrade()
  }

  const formatPrice = () => {
    if (subscription.priceInCents === 0) return 'Free'
    return `$${subscription.priceInCents / 100}`
  }

  return (
    <Dialog open={opened} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <PrimaryButton size="lg" className="mt-auto w-full transform transition-all duration-200 hover:shadow-lg">
          Select
        </PrimaryButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Typography variant="xl" className="text-green-50">
              {showAddPayment ? 'Add Payment Method' : 'Upgrade Your Plan'}
            </Typography>
          </DialogTitle>
        </DialogHeader>

        {showAddPayment ? (
          <div className="mt-4">
            <Typography variant="base" className="mb-4 text-gray-50">
              Please add a payment method to continue with the upgrade
            </Typography>
            <PaymentForm onSuccess={handlePaymentSuccess} />
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-6">
              <Typography variant="base" className="text-center text-gray-50">
                {subscription.priceInCents > 0
                  ? subscription.interval === 'month'
                    ? 'Billed monthly. Cancel anytime.'
                    : 'Billed annually. Save 17% compared to monthly.'
                  : ''}
              </Typography>

              <div className="flex flex-col rounded-lg bg-light-green-200 p-4">
                <div className="mb-4 flex flex-row justify-between">
                  <Typography variant="2xl" className="text-gray-900">
                    {subscription.name}
                  </Typography>
                  <div className="flex flex-row items-baseline">
                    <Typography variant="2xl-semibold" className="text-gray-900">
                      {formatPrice()}
                    </Typography>
                    {subscription.priceInCents > 0 && (
                      <Typography variant="base" className="text-gray-900">
                        /{subscription.interval}
                      </Typography>
                    )}
                  </div>
                </div>
                <button className="flex flex-row items-center gap-2" onClick={() => setOpened(false)}>
                  <Typography variant="base-semibold" className="text-left text-light-green-500 underline">
                    Change Plan
                  </Typography>
                </button>
              </div>

              {subscription.priceInCents > 0 && (
                <>
                  {methods?.length ? (
                    methods.map((method) => <PaymentMethod key={method.id} method={method} isDelete />)
                  ) : (
                    <Typography variant="base" className="text-gray-50">
                      No payment methods added yet
                    </Typography>
                  )}
                </>
              )}
            </div>

            <DialogFooter className="mt-6 sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setSupportDialogOpen(!supportDialog)
                  setOpened(false)
                }}
                className="flex flex-row items-center space-x-2 text-light-green-50"
              >
                <IconHelpCircle />
                <span>Support</span>
              </button>
              <PrimaryButton
                onClick={handleUpgrade}
                isLoading={isLoading}
                className="w-40 bg-light-green-500 hover:bg-light-green-600 active:bg-light-green-700"
              >
                Upgrade
              </PrimaryButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UpgradePlanDialog
